import {
  BarChartIcon,
  EllipsisText,
  Flex,
  Loading,
  NoRowsOverlay,
  Table,
  TableContainer,
  Text,
} from '@shared/components';
import { TableBody, TableHeader, type TableBodyRow } from '@shared/table-generator';
import { useMemo } from 'react';

import { useExamEventScoreReport } from '../../providers/exam-event-score-report-provider/use-exam-event-score-report';

import { getColumns } from './columns';
import { createScoreReportTableRows, createTableBodyRows } from './utils/score-report-table';

export default function ExamEventScoreReportTable() {
  const {
    scoreReports,
    hasFilters,
    selectedTagCategory,
    selectedCourseLabel,
    isLoading,
    orderedQConfigStructures,
  } = useExamEventScoreReport();

  const orderedStructures = useMemo(() => {
    return [...(orderedQConfigStructures ?? [])].sort(
      (a, b) => a.calculatedOrder - b.calculatedOrder,
    );
  }, [orderedQConfigStructures]);

  const orderedScoreColumns = useMemo(() => {
    return orderedStructures.map((structure, index) => ({
      id: structure.qConfigStructure.id,
      label: `${index + 1}`,
    }));
  }, [orderedStructures]);

  const shouldShowLoadingState = isLoading && hasFilters;

  const columns = useMemo(() => {
    const shouldHideGroupedScoreHeader = !isLoading && !hasFilters;

    return getColumns(shouldHideGroupedScoreHeader ? [] : orderedScoreColumns, selectedTagCategory);
  }, [hasFilters, isLoading, orderedScoreColumns, selectedTagCategory]);

  const scoreReportRows = useMemo(() => {
    return createScoreReportTableRows({
      scoreReports,
      orderedQConfigStructures: orderedStructures,
      selectedCourseLabel,
      selectedTagCategory,
    });
  }, [orderedStructures, scoreReports, selectedCourseLabel, selectedTagCategory]);

  const rows = useMemo<TableBodyRow[]>(() => {
    const tableRows = createTableBodyRows({
      rows: scoreReportRows,
      visibleTagCount: orderedScoreColumns.length,
    });

    return tableRows.map(
      (row): TableBodyRow => ({
        ...row,
        children: row.children.map((cell, index) => {
          const isNameColumn = index === 1;
          const isLastTwoColumns =
            index === row.children.length - 2 || index === row.children.length - 1;

          if (isNameColumn) {
            return {
              ...cell,
              children:
                typeof cell.children === 'string' ? (
                  <EllipsisText>{cell.children}</EllipsisText>
                ) : (
                  cell.children
                ),
            };
          }

          if (isLastTwoColumns) {
            return {
              ...cell,
              children:
                typeof cell.children === 'string' || typeof cell.children === 'number' ? (
                  <Text>{cell.children}</Text>
                ) : (
                  cell.children
                ),
            };
          }

          return {
            ...cell,
            children:
              typeof cell.children === 'string' || typeof cell.children === 'number' ? (
                <Text>{cell.children}</Text>
              ) : (
                cell.children
              ),
          };
        }),
      }),
    );
  }, [orderedScoreColumns.length, scoreReportRows]);

  const shouldShowEmptyState = !isLoading && (!hasFilters || rows.length === 0);

  const emptyRows = useMemo<TableBodyRow[]>(() => {
    return [
      {
        id: 'exam-event-score-report-empty-state',
        children: [
          {
            children: (
              <NoRowsOverlay
                icon={BarChartIcon}
                title='ยังไม่มีข้อมูลให้แสดง'
                description='โปรดเลือกหลักสูตรและประเภทคะแนน เพื่อดูผลคะแนนตามเงื่อนไขที่ต้องการ'
                slotProps={{
                  wrapper: {
                    sx: {
                      py: 20,
                    },
                  },
                  icon: {
                    fontSize: 96,
                    color: 'secondary',
                  },
                }}
              />
            ),
            colSpan: columns[0].children.length,
            sx: {
              p: 0,
              borderBottom: 'none',
            },
          },
        ],
      },
    ];
  }, [columns]);

  return (
    <TableContainer
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.shape.rounded(2),
        backgroundColor: theme.palette.common.white,
        overflowX: 'auto',
        overflowY: 'hidden',
      })}
    >
      <Table sx={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
        <TableHeader
          columns={columns}
          slotProps={{
            tableHeaderRow: {
              sx: (theme) => ({
                height: '40px',
                '& > th': {
                  height: '40px',
                  borderRight: `1px solid ${theme.palette.grey[300]}`,
                  borderBottom: `1px solid ${theme.palette.grey[300]}`,
                },
              }),
            },
            tableHeaderCell: {
              sx: (theme) => ({
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.text.primary,
                boxSizing: 'border-box',
                display: 'table-cell',
                height: '40px',
                minHeight: '40px',
                maxHeight: '40px',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '20px',
                px: theme.spacing(1.5),
                py: 0,
                verticalAlign: 'middle',
                whiteSpace: 'nowrap',
              }),
            },
          }}
        />

        {shouldShowLoadingState ? (
          <TableBody
            rows={[
              {
                id: 'exam-event-score-report-loading-state',
                children: [
                  {
                    children: (
                      <Flex sx={{ width: '100%', py: 20, justifyContent: 'center' }}>
                        <Loading />
                      </Flex>
                    ),
                    colSpan: columns[0].children.length,
                    sx: {
                      p: 0,
                      borderBottom: 'none',
                    },
                  },
                ],
              },
            ]}
          />
        ) : shouldShowEmptyState ? (
          <TableBody rows={emptyRows} />
        ) : (
          <TableBody
            rows={rows}
            slotProps={{
              tableBodyCell: {
                sx: (theme) => ({
                  ...theme.typography.body1,
                  px: theme.spacing(3),
                  py: theme.spacing(2.5),
                  borderRight: `1px solid ${theme.palette.grey[300]}`,
                  borderBottom: `1px solid ${theme.palette.grey[300]}`,
                  verticalAlign: 'middle',
                }),
              },
              tableBodyRow: {
                sx: {
                  '& > td:last-of-type': {
                    borderRight: 'none',
                  },
                  '&:last-of-type > td': {
                    borderBottom: 'none',
                  },
                },
              },
            }}
          />
        )}
      </Table>
    </TableContainer>
  );
}
